import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { sessionQueryOptions, unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { cartStoreFinderPageQueryOptions } from "~/app/api/pageData/cartStoreFinderPageQuery";
import type {
  CartStoreFinderCompetenceData,
  CartStoreFinderPageInitialData,
} from "~/app/api/pageData/cartStoreFinderPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createSalesPartnerPage, type ConfigurationData } from "~/features/salesPartner/components/SalesPartnerPage";
import { clearSalesPartnerForActiveB2CCart } from "~/features/cart/cart/api/clearSalesPartner";
import { activeCartResourceId } from "~/domains/cart/utils/cartResourceIdentity";
import { userRolesAreAuthenticated, userRolesAreRegistered } from "~/domains/user/utils/roles";
import { occCartUserPathFor } from "~/app/api/occ/occUserPath";

function toCompetences(competences: CartStoreFinderCompetenceData[] | null | undefined) {
  if (!competences) {
    return [];
  }
  return competences.map((competence) => ({ key: competence.key, label: competence.label }));
}

export function toCartStoreFinderProps(data: CartStoreFinderPageInitialData | undefined): ConfigurationData {
  const selected = data?.selectedProductCompetences;
  return {
    shareLinearConfigurationData: [],
    productCompetences: toCompetences(data?.productCompetences),
    certifiedCompetences: toCompetences(data?.certifiedCompetences),
    selectedProductCompetences: selected && selected.length > 0 ? selected : [],
    googleMapsApiKey: data?.googleMapsApiKey,
  };
}

function CartStoreFinderRoute() {
  const result = useLoaderData({
    from: "/cart/store-finder",
  }) as PageInitialData<CartStoreFinderPageInitialData>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createSalesPartnerPage(toCartStoreFinderProps(result.data))}
    </StorefrontShellFrame>
  );
}

export async function loadCartStoreFinderPageData(context: { queryClient: QueryClient }) {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions());
  const roles = session.user.roles;
  const authenticated = userRolesAreAuthenticated(roles);
  const userPath = occCartUserPathFor(authenticated);
  const activeCartId = activeCartResourceId(session.activeCart, authenticated);
  await clearSalesPartnerForActiveB2CCart({
    activeCartId,
    customer: authenticated && !userRolesAreRegistered(roles),
    userPath,
  });
  const result = await context.queryClient.ensureQueryData(cartStoreFinderPageQueryOptions(userPath, activeCartId));

  return unwrapPageInitialData(result);
}

export const cartStoreFinderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cart/store-finder",
  loader: async ({ context }) => loadCartStoreFinderPageData(context),
  component: CartStoreFinderRoute,
});
